import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, CreditCard, Gauge, Megaphone, Settings, Users } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Sparkbars } from "../../../components/ops/Sparkbars";
import { StatCard } from "../../../components/ops/StatCard";
import { todayLabel } from "../../../utils/greeting";
import { upcomingCounts } from "../../../utils/trends";

const quickActions = [
  { to: "/dashboard/sessions", label: "Book a session", description: "Reserve an upcoming training slot", icon: CalendarClock },
  { to: "/dashboard/membership", label: "Manage membership", description: "Review your plan and enroll a player", icon: CreditCard },
  { to: "/dashboard/settings", label: "Account settings", description: "Update your profile details", icon: Settings },
];

export function DashboardOverviewPage() {
  const { accessToken, user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      api.get("/dashboard/summary"),
      api.get("/schedule/bookings"),
      api.get("/memberships/enrollments"),
    ])
      .then(([summaryResponse, bookingsResponse, enrollmentsResponse]) => {
        if (!active) return;
        setPlayers(summaryResponse.data.players || []);
        setBookings(bookingsResponse.data.bookings || []);
        setEnrollments(enrollmentsResponse.data.enrollments || []);
      })
      .catch(() => { if (active) setError("Unable to load your dashboard"); })
      .finally(() => { if (active) setLoading(false); });

    api.get("/announcements")
      .then((response) => { if (active) setAnnouncements(response.data.announcements || []); })
      .catch(() => {});

    return () => { active = false; };
  }, [accessToken]);

  const stats = useMemo(() => {
    const now = Date.now();
    const upcoming = bookings.filter((b) => b.status === "booked" && b.session && new Date(b.session.startsAt).getTime() > now);
    const activeMemberships = enrollments.filter((e) => e.status === "active" || e.status === "pending");
    const assessed = players.filter((entry) => entry.latestAssessment);
    const avgIndex = assessed.length
      ? Math.round(assessed.reduce((sum, entry) => sum + (entry.formulaPlayerIndex || 0), 0) / assessed.length)
      : null;

    return {
      totalPlayers: players.length,
      upcomingCount: upcoming.length,
      membershipCount: activeMemberships.length,
      unassessed: players.length - assessed.length,
      avgIndex,
    };
  }, [players, bookings, enrollments]);

  const sessionsThisWeek = useMemo(
    () => upcomingCounts(bookings.filter((b) => b.status === "booked").map((b) => b.session)),
    [bookings],
  );

  return (
    <div>
      <PageHeader title={`Welcome back, ${user?.firstName || "there"}`} subtitle={`${todayLabel()} · Here's how your players are progressing`} />

      {announcements.length > 0 && (
        <div className="ops-announcement-list">
          {announcements.map((announcement) => (
            <div key={announcement._id} className="ops-announcement">
              <Megaphone size={16} />
              <div>
                <p className="ops-announcement-title">{announcement.title}</p>
                <p className="ops-announcement-body">{announcement.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ops-stat-grid">
        <StatCard icon={Users} label="My players" value={loading ? "—" : stats.totalPlayers} hint="Registered on your account" />
        <StatCard icon={CalendarClock} label="Upcoming sessions" value={loading ? "—" : stats.upcomingCount} hint="Booked and confirmed" />
        <StatCard icon={CreditCard} label="Memberships" value={loading ? "—" : stats.membershipCount} hint="Active or pending" />
        <StatCard icon={Gauge} label="Avg. Formula Player Index" value={loading ? "—" : stats.avgIndex ?? "—"} hint="Latest assessments" />
      </div>

      {error && <div className="ops-alert ops-alert--error">{error}</div>}

      <div className="ops-columns">
        <div className="ops-card">
          <h2 className="ops-card-title">Your players</h2>
          {loading ? (
            <p className="ops-loading">Loading your players…</p>
          ) : players.length === 0 ? (
            <EmptyState icon={Users} title="No players yet" description="Add a player to start your development journey." />
          ) : (
            <ul className="ops-list">
              {players.map(({ player, formulaPlayerIndex, attendanceRate }) => (
                <li key={player._id} className="ops-list-item">
                  <div className="ops-list-item-avatar">{player.firstName?.charAt(0)}{player.lastName?.charAt(0)}</div>
                  <div className="ops-list-item-body">
                    <p className="ops-list-item-title">{player.firstName} {player.lastName}</p>
                    <p className="ops-list-item-meta">
                      Ages {player.ageGroup} · {attendanceRate != null ? `${attendanceRate}% attendance` : "No attendance yet"}
                    </p>
                  </div>
                  <div className="ops-list-item-trailing">
                    <Badge tone={formulaPlayerIndex != null ? "success" : "neutral"}>
                      {formulaPlayerIndex != null ? `FPI ${formulaPlayerIndex}` : "Not assessed"}
                    </Badge>
                    <Link to={`/dashboard/players/${player._id}/report`} className="ops-button ops-button--outline ops-button--small">
                      Report
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="ops-stack">
          <div className="ops-card">
            <div className="ops-activity-header">
              <h2 className="ops-card-title" style={{ margin: 0 }}>This week's sessions</h2>
              {!loading && <Sparkbars data={sessionsThisWeek} direction="future" />}
            </div>
            <p className="ops-activity-total">
              <strong>{loading ? "—" : stats.upcomingCount}</strong> {stats.upcomingCount === 1 ? "session" : "sessions"} booked over the next 7 days
            </p>

            <ul className="ops-attention-list">
              {!loading && stats.membershipCount === 0 && (
                <li>
                  <Link to="/dashboard/membership" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      No active membership — enroll a player
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.upcomingCount === 0 && (
                <li>
                  <Link to="/dashboard/sessions" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      No sessions booked yet
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.unassessed > 0 && (
                <li>
                  <span className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot ops-attention-item-dot--ok" />
                      {stats.unassessed} {stats.unassessed === 1 ? "player hasn't" : "players haven't"} been assessed yet
                    </span>
                  </span>
                </li>
              )}
              {!loading && stats.membershipCount > 0 && stats.upcomingCount > 0 && stats.unassessed === 0 && (
                <li className="ops-attention-empty">You're all caught up — nothing needs attention.</li>
              )}
            </ul>
          </div>

          <div className="ops-card">
            <h2 className="ops-card-title">Quick actions</h2>
            <div className="ops-action-grid">
              {quickActions.map(({ to, label, description, icon: Icon }) => (
                <Link key={to} to={to} className="ops-action-card">
                  <div className="ops-action-card-icon">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div className="ops-action-card-content">
                    <h3>{label}</h3>
                    <p>{description}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Link to="/dashboard/sessions" className="ops-card-link">
              View all upcoming sessions <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
